import { t } from 'i18next';

import { Fragment, createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AddressInput from 'components/field/AddressInput';
import Badge from 'components/field/Badge';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Radio from 'components/global/Radio';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import {
	getCityByCountry,
	getCityByState,
	getCountryList,
	getDistrictByCity,
	getNeighbourhoodByDistrict,
	getStateByCountry
} from 'stores/globalAddressSlice';

import AppLogger from 'utils/AppLogger';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import AutoString from './AutoString';
import ComplexCityComponent from './ComplexCityComponent';
import EnumSelect from './EnumSelect';

/**
 * @typedef {object} AddressGroupV2Props
 * @property {string} [formFieldName] - Form field to bound this component's children.
 * @property {object} [fieldsColspan] - Field config to show/hide, and scale column extension of the fields.
 * @property {object} [requiredFields] - Required fields config.
 * @property {object} [formMethods] - Form methods from the parent component.
 * @property {boolean} [disabled] - If true, the form item is disabled.
 * @property {boolean} [isLoading] - If true, the form item is loading.
 * @property {number} [colSpan] - Column span of the form item.
 * @property {object} [labels]  - Labels for the fields. country | state | city | district (optional)
 * @property {boolean} [selectFromMap] - If true, the map button is enabled.
 * @property {boolean} [lineSeperator] - If true, the line seperator is shown.
 * @property {object} [restrictions] - Restrictions for the fields.
 * @property {Array} [variants] - Variants for the address group. shortNationalAddress | fullTextAddress | fillFromGoogleMaps
 * @property {string} [formMode] - Form mode.
 * @property {string} [autoFillID] - Auto fill fields.
 * @property {number} [mapZoom] - Map zoom level.
 * @property {boolean} [centerAddressField] - If true, the address field is centered.
 * @param {*} props
 * @return {*}
 */
const AddressGroupV2 = (props) => {
	// Check if formFieldName is defined
	const {
		formFieldName,
		variants = ['shortNationalAddress', 'fullTextAddress', 'fillFromGoogleMaps'],
		formMode,
		autoFillID,
		mapZoom,
		centerAddressField = false
	} = props;
	if (!formFieldName) AppLogger.error('Please define the "formFieldName" for AddressGroupV2');

	const formMethods = useFormContext();
	if (!formMethods) throw new Error('AddressGroupV2: missing form context!');
	const { getValues } = formMethods;

	const dispatch = useDispatch();

	const [selectedVariant, setSelectedVariant] = useState(
		['edit', 'view'].includes(formMode)
			? (getValues(formFieldName)?.addressInputType ?? 'fullTextAddress')
			: 'fullTextAddress'
	);
	const [isShowAddressDetails, setIsShowAddressDetails] = useState(['edit', 'view'].includes(formMode));

	useEffect(() => {
		// This effect ensures that address details are displayed correctly when navigating back and forth in step-based forms like the add order form, after selecting an address and moving to the next step and then returning to the previous step.
		if (formMode === 'add' && getValues(formFieldName)?.countryName && getValues(formFieldName)?.text) {
			setIsShowAddressDetails(true);
		}

		dispatch(getCountryList());
	}, []);

	// Render
	return (
		<View
			{...props}
			selectedVariant={selectedVariant}
			setSelectedVariant={setSelectedVariant}
			isShowAddressDetails={isShowAddressDetails}
			setIsShowAddressDetails={setIsShowAddressDetails}
			variants={variants}
			autoFillID={autoFillID}
			formMode={formMode}
			mapZoom={mapZoom}
			centerAddressField={centerAddressField}
		/>
	);
};

const View = ({
	formFieldName = null,
	requiredFields = {},
	disabledFields = {},
	disabled,
	isLoading = false,
	labels = {},
	restrictions = null,
	variants,
	selectedVariant,
	setSelectedVariant,
	isShowAddressDetails,
	setIsShowAddressDetails,
	mapRef,
	autoFillID,
	formMode,
	mapZoom,
	centerAddressField
}) => {
	const {
		address: addressRequired = true,
		country: countryRequired = true,
		state: stateRequired = false,
		city: cityRequired = true,
		district: districtRequired = false,
		neighbourhood: neighbourhoodRequired = false,
		street: streetRequired = false,
		building: buildingRequired = false,
		zipCode: zipCodeRequired = false
	} = requiredFields;

	const {
		address: addressDisabled = false,
		country: countryDisabled = false,
		state: stateDisabled = false,
		city: cityDisabled = false,
		district: districtDisabled = false,
		neighbourhood: neighbourhoodDisabled = false,
		street: streetDisabled = false,
		building: buildingDisabled = false,
		zipCode: zipCodeDisabled = false
	} = disabledFields;

	const {
		country: countryLabel = 'country',
		state: stateLabel = 'stateOrRegion',
		city: cityLabel = 'city',
		district: districtLabel = 'district',
		neighbourhood: neighbourhoodLabel = 'neighbourhood',
		street: streetLabel = 'street',
		building: buildingLabel = 'building',
		zipCode: zipCodeLabel = 'zipCode'
	} = labels;

	// form Methods
	const formMethods = useFormContext();
	const { control, setValue } = formMethods;
	const dispatch = useDispatch();

	const setSelectedValue = (name, value) =>
		setValue(`${formFieldName}.${name}`, value ?? null, {
			shouldValidate: true,
			shouldDirty: true
		});

	const countryList = useSelector((state) => state?.globalAddress?.countryList);

	// watched Fields
	const { countryName, stateName, cityName, districtName, neighbourhoodName, addressInputType } =
		useWatch({
			control,
			name: formFieldName
		}) ?? {};

	const countryObject = useMemo(() => {
		if (countryList?.length > 0 && countryName) {
			return countryList?.find((item) => item?.shortCode === countryName);
		}
	}, [countryName, countryList]);

	const resetFields = useCallback((fields) => {
		fields?.forEach((field) => {
			setValue(`${formFieldName}.${field}`, null);
		});
	}, []);

	useEffect(() => {
		if (addressInputType) {
			setSelectedVariant(addressInputType);
			if (!isShowAddressDetails) {
				setIsShowAddressDetails(!!autoFillID);
			}
		}
	}, [autoFillID, addressInputType]);

	useEffect(() => {
		if (countryObject?.hasState === 'no') {
			resetFields(['stateName']);
		}
	}, [countryObject?.hasState]);

	useEffect(() => {
		if (countryObject?.hasNeighbourhood === 'no') {
			resetFields(['neighbourhoodName']);
		}
	}, [countryObject?.hasNeighbourhood]);

	useEffect(() => {
		// In the case of add or undefined form mode, the first variant is selected.
		if ((formMode === 'add' || !formMode) && !addressInputType) {
			setSelectedVariant(countryObject?.hasShortAddress === 'yes' ? 'shortNationalAddress' : 'fullTextAddress');
		}
	}, [formMode, countryObject]);

	const showVariants = useMemo(() => selectedVariant || !!countryName, [selectedVariant, countryName]);
	const renderAddressDetailsFields = () => (
		<F.FormItemContainer noLeftPadding justifyContent={centerAddressField ? 'center' : 'unset'}>
			{countryObject?.hasState === 'yes' && countryObject?.useComplexCityComponent === 'no' && (
				<FormItem
					col={6}
					label={stateLabel}
					isLoading={isLoading}
					required={stateRequired}
					style={{
						margin: 'unset !important'
					}}>
					{createElement(countryObject?.allowStateCustomValue === 'yes' ? AutoString : EnumSelect, {
						name: `${formFieldName}.stateName`,
						placeholder: stateLabel,
						fetchFunction: async () => {
							return await dispatch(getStateByCountry(countryName))
								.unwrap()
								.then((response) => {
									return response?.data?.map((item) => item.name);
								});
						},
						value: t(stateName) ?? null,
						setValue: (value) => {
							setSelectedValue('stateName', value);
							resetFields(['cityName', 'districtName', 'neighbourhoodName']);
						},
						disabled: !countryName || stateDisabled || disabled
					})}
				</FormItem>
			)}

			<FormItem
				col={6}
				label={cityLabel}
				isLoading={isLoading}
				required={cityRequired}
				style={{
					margin: 'unset !important'
				}}>
				{countryObject?.useComplexCityComponent !== 'yes' &&
					createElement(countryObject?.allowCityCustomValue === 'yes' ? AutoString : EnumSelect, {
						name: `${formFieldName}.cityName`,
						placeholder: cityLabel,
						fetchFunction: async () => {
							if (stateName) {
								return await dispatch(getCityByState(stateName))
									.unwrap()
									.then((response) => {
										return response?.data?.map((item) => item.name);
									});
							} else {
								return await dispatch(getCityByCountry(countryName))
									.unwrap()
									.then((response) => {
										return response?.data?.map((item) => item.name);
									});
							}
						},
						value: t(cityName) ?? null,
						setValue: (value) => {
							setSelectedValue('cityName', value);
							resetFields(['districtName', 'neighbourhoodName']);
						},
						disabled:
							!countryName ||
							cityDisabled ||
							(countryObject?.hasState === 'yes' && stateRequired && !stateName) ||
							disabled
					})}
				{countryObject?.useComplexCityComponent === 'yes' && (
					<ComplexCityComponent
						name={`${formFieldName}.cityName`}
						placeholder={cityLabel}
						countryName={countryName}
						value={t(cityName) ?? null}
						setValue={(value) => {
							setSelectedValue('stateName', value?.stateName);
							// setSelectedValue('governorateName', value?.governorateName);
							setSelectedValue('cityName', value?.name);
							resetFields(['districtName', 'neighbourhoodName']);
						}}
						disabled={!countryName || cityDisabled || disabled}
					/>
				)}
			</FormItem>

			<FormItem
				col={6}
				label={districtLabel}
				isLoading={isLoading}
				required={districtRequired}
				style={{
					margin: 'unset !important'
				}}>
				{createElement(countryObject?.allowDistrictCustomValue === 'yes' ? AutoString : EnumSelect, {
					name: `${formFieldName}.districtName`,
					placeholder: districtLabel,
					fetchFunction: async () => {
						return await dispatch(getDistrictByCity(cityName))
							.unwrap()
							.then((response) => {
								return response?.data?.map((item) => item.name);
							});
					},
					value: t(districtName) ?? null,
					setValue: (value) => {
						setSelectedValue('districtName', value);
						resetFields(['neighbourhoodName']);
					},
					disabled: !cityName || districtDisabled || disabled
				})}
			</FormItem>
			{countryObject?.hasNeighbourhood === 'yes' && (
				<FormItem
					col={6}
					label={neighbourhoodLabel}
					isLoading={isLoading}
					required={neighbourhoodRequired}
					style={{
						margin: 'unset !important'
					}}>
					{createElement(countryObject?.allowNeighbourhoodCustomValue === 'yes' ? AutoString : EnumSelect, {
						name: `${formFieldName}.neighbourhoodName`,
						placeholder: neighbourhoodLabel,
						fetchFunction: async () => {
							return await dispatch(getNeighbourhoodByDistrict(districtName))
								.unwrap()
								.then((response) => {
									return response?.data?.map((item) => item.name);
								});
						},
						value: t(neighbourhoodName) ?? null,
						setValue: (value) => {
							setSelectedValue('neighbourhoodName', value);
						},
						disabled: !countryName || neighbourhoodDisabled || disabled
					})}
				</FormItem>
			)}

			<FormItem
				col={6}
				label={streetLabel}
				isLoading={isLoading}
				required={streetRequired}
				style={{
					margin: 'unset !important'
				}}>
				<StringInput
					name={`${formFieldName}.street`}
					placeholder={streetLabel}
					disabled={!countryName || streetDisabled || disabled}
				/>
			</FormItem>

			<FormItem
				col={6}
				label={buildingLabel}
				isLoading={isLoading}
				required={buildingRequired}
				style={{
					margin: 'unset !important'
				}}>
				<StringInput
					name={`${formFieldName}.buildingName`}
					placeholder={buildingLabel}
					disabled={!countryName || buildingDisabled || disabled}
				/>
			</FormItem>

			<FormItem
				col={6}
				label={zipCodeLabel}
				isLoading={isLoading}
				required={zipCodeRequired || countryObject?.zipCodeRequired === 'yes'}
				style={{
					margin: 'unset !important'
				}}>
				<StringInput name={`${formFieldName}.zipCode`} disabled={!countryName || zipCodeDisabled || disabled} />
			</FormItem>
		</F.FormItemContainer>
	);

	const renderMapByVariantContent = (variant, countryName) => {
		const labelSelector = (label, variant) => {
			if (label) {
				if (variant === 'shortNationalAddress') return 'shortNationalAddress';
				if (variant === 'fullTextAddress') return 'fullAddressLine';
			}
		};

		const renderAddressItem = (label) => (
			<FormItem col={12} label={labelSelector(label, variant)} isLoading={isLoading} required={addressRequired}>
				<AddressInput
					showButton={false}
					restrictCountrySearch={
						selectedVariant === 'shortNationalAddress' && countryObject?.hasShortAddress === 'yes'
					}
					placeholder={`${selectedVariant}Placeholder`}
					selectedCountry={countryName}
					disabled={restrictions?.address?.isNotDifferent || addressDisabled || disabled}
					name={formFieldName}
					restrictions={restrictions}
					isGetLatLng
					onAddressChange={() => {
						setIsShowAddressDetails(true);
					}}
					componentVersion="V2"
					variantForAddressGroup={variant}
					{...(variant === 'fillFromGoogleMaps' && {
						withMap: true,
						mapProps: {
							mapRef,
							defaultCountry: countryName,
							fullscreenControl: true,
							zoom: mapZoom ?? 8,
							showAutocomplete: true,
							disabled: formMode === 'view' || disabled
						},
						className: 'map-field'
					})}
				/>
			</FormItem>
		);

		const tooltipTitle = () => (
			<F.TooltipContainer>
				<Image
					url="https://storage.googleapis.com/tryoto-public/public/assets/images/addressInfo.svg"
					height="60px"
					width="138px"
				/>

				<Typography component="p" color="white" noWrap>
					<Trans
						i18nKey="nationalAddressDesc"
						components={{
							bold: <F.BoldText />
						}}
					/>
				</Typography>

				<Typography component="p" color="white" noWrap>
					<Trans
						i18nKey="shortAddressDesc"
						components={{
							bold: <F.BoldText />
						}}
					/>
				</Typography>
			</F.TooltipContainer>
		);

		const renderRadioItem = (label, variantType) => (
			<FormItem>
				<F.RadioItem
					disabled={disabled}
					readOnly
					onClick={() => {
						setSelectedVariant(variantType);
						setSelectedValue('addressInputType', variantType);
					}}>
					<Radio disabled={disabled} checked={selectedVariant === variantType} readOnly />
					<F.RadioLabelContainer>
						<Typography fontWeight="500">{label}</Typography>
						{variantType === 'shortNationalAddress' && (
							<F.InfoWrapper>
								<Tooltip title={tooltipTitle()} placement="bottom">
									<Icon icon={faCircleInfo} />
								</Tooltip>
								<Badge variant="pill" size="small" color="red" text="new" />
							</F.InfoWrapper>
						)}
					</F.RadioLabelContainer>
				</F.RadioItem>
			</FormItem>
		);

		const shouldRenderRadio = variants.length > 1;
		const shouldRenderFormItem = selectedVariant === variant || variants.length === 1;

		if (
			variant === 'shortNationalAddress' &&
			(countryObject?.hasShortAddress === 'yes' ||
				(formMode === 'view' && selectedVariant === 'shortNationalAddress'))
		) {
			return (
				<>
					{shouldRenderRadio && renderRadioItem('shortNationalAddress', 'shortNationalAddress')}
					{shouldRenderFormItem && (
						<F.FormItemContainer noLeftPadding>
							{renderAddressItem('shortNationalAddress')}
						</F.FormItemContainer>
					)}
				</>
			);
		}

		if (variant === 'fullTextAddress') {
			return (
				<>
					{shouldRenderRadio && renderRadioItem('fullTextAddress', 'fullTextAddress')}
					{shouldRenderFormItem && (
						<F.FormItemContainer noLeftPadding>{renderAddressItem('fullTextAddress')}</F.FormItemContainer>
					)}
				</>
			);
		}

		if (variant === 'fillFromGoogleMaps') {
			return (
				<>
					{shouldRenderRadio && renderRadioItem('fillFromGoogleMaps', 'fillFromGoogleMaps')}
					{shouldRenderFormItem && (
						<F.FormItemContainer noLeftPadding>
							{renderAddressItem('fillFromGoogleMaps')}
						</F.FormItemContainer>
					)}
				</>
			);
		}
	};

	// Main Render
	return (
		<>
			<FormItem colSpan={12} label={countryLabel} isLoading={isLoading} required={countryRequired}>
				<EnumSelect
					name={`${formFieldName}.countryName`}
					placeholder="country"
					data={countryList.map((item) => item?.shortCode) ?? []}
					renderSelectionValue={(value) => (
						<F.FlagOptionContainer className="selected">
							<span className={`oto-country-flag flag-${countryName?.toString()?.toLowerCase()}`} />
							<Typography>{value ?? null}</Typography>
						</F.FlagOptionContainer>
					)}
					renderOption={(value) => (
						<F.FlagOptionContainer>
							<span className={`oto-country-flag flag-${value?.toLowerCase()}`} />
							<Typography>{`country.${value?.toUpperCase()}`}</Typography>
						</F.FlagOptionContainer>
					)}
					value={t(countryName) ?? restrictions?.country?.code?.toUpperCase() ?? null}
					setValue={(value) => {
						if (restrictions?.country?.isNotDifferent) return;
						if (value === countryName) return;
						setSelectedValue('countryName', value);
						resetFields([
							'stateName',
							'cityName',
							'districtName',
							'street',
							'buildingName',
							'zipCode',
							'text',
							'lat',
							'lng',
							'countryID',
							'cityID'
						]);
					}}
					disabled={restrictions?.country?.isNotDifferent || countryDisabled || disabled}
					labelPrefix="country."
					valueRemovable={false}
				/>
			</FormItem>

			{showVariants &&
				variants.map((variant, index) => (
					<Fragment key={variant}>
						{renderMapByVariantContent(variant, countryName)}
						{selectedVariant === variant &&
							countryName &&
							(selectedVariant === 'fullTextAddress' || isShowAddressDetails) &&
							renderAddressDetailsFields()}
						{index < variants.length - 1 && variants.length > 1 && <F.Line className="line" />}
					</Fragment>
				))}
		</>
	);
};

export default AddressGroupV2;
